// src/components/kiosk/POSSection/ReceiptPrinterBW.jsx
// MAC CHROME OPTIMIZED - DIRECT PRINT DIALOG
// No popup window - just fast print dialog
// Optimized for Chrome on Mac with strategic rendering delays

import QRCode from 'qrcode';

class ReceiptPrinterBW {
  constructor() {
    this.businessInfo = {
      name: "ELLA'S PLACE",
      tagline: "Salon, Spa & Ladies Gym",
      address: "16 Ibrahim Jallo Waziri Street",
      city: "Apo, Abuja",
      phone: "09055444495",
      website: "ellasplace.net",
      email: "info@ellasplace.net"
    };
    
    // Inline SVG logo (optimized from PNG)
    this.logoSVG = `
      <svg width="60" height="60" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
        <!-- Dark green circle background -->
        <circle cx="100" cy="100" r="98" fill="#1a3d1a" stroke="#000" stroke-width="2"/>
        
        <!-- Ella's text in gold/orange -->
        <text x="100" y="95" font-family="'Brush Script MT', cursive, serif" font-size="48" 
              fill="#d4a574" text-anchor="middle" font-style="italic" font-weight="bold">
          Ella's
        </text>
        
        <!-- PLACE text in purple/mauve -->
        <text x="100" y="130" font-family="Arial, sans-serif" font-size="28" 
              fill="#8b7d9e" text-anchor="middle" font-weight="bold" letter-spacing="2">
          PLACE
        </text>
        
        <!-- Small leaf decoration (top right) -->
        <ellipse cx="130" cy="50" rx="8" ry="15" fill="#8b7d9e" transform="rotate(-20 130 50)"/>
        <ellipse cx="138" cy="48" rx="6" ry="12" fill="#9d8fb0" transform="rotate(-15 138 48)"/>
        <ellipse cx="125" cy="55" rx="5" ry="10" fill="#7a6d8c" transform="rotate(-25 125 55)"/>
      </svg>
    `;
  }

  // Extract first name from user data (same logic as POSHeader)
  getDisplayName(cashierData) {
    // Priority 1: If already a simple first name, use it
    if (!cashierData || typeof cashierData !== 'string') {
      return 'Staff';
    }
    
    // Priority 2: Extract from username (before first dot)
    if (cashierData.includes('.')) {
      const firstName = cashierData.split('.')[0];
      // Capitalize first letter, lowercase rest
      return firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
    }
    
    // Priority 3: No dots, capitalize the name
    return cashierData.charAt(0).toUpperCase() + cashierData.slice(1).toLowerCase();
  }

  // Pre-generate QR code
  async generateQRCodeBase64(text) {
    try {
      console.time('⚡ QR Generation');
      const qrDataURL = await QRCode.toDataURL(text, {
        width: 100,
        margin: 1,
        errorCorrectionLevel: 'M',
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      console.timeEnd('⚡ QR Generation');
      return qrDataURL;
    } catch (err) {
      console.error('QR generation failed:', err);
      return null;
    }
  }

  async print(receiptData) {
    try {
      console.time('⚡ TOTAL PRINT TIME');
      
      // STEP 1: Pre-generate QR code
      const qrCodeURL = `https://ellasplace.net`;
      const preGeneratedQR = await this.generateQRCodeBase64(qrCodeURL);
      
      // STEP 2: Generate optimized HTML
      console.time('⚡ HTML Generation');
      const html = await this.generateReceiptHTML(receiptData, preGeneratedQR);
      console.timeEnd('⚡ HTML Generation');
      
      // STEP 3: Direct print (NO POPUP)
      console.time('⚡ Print Dialog Time');
      await this.printDirectly(html);
      console.timeEnd('⚡ Print Dialog Time');
      
      console.timeEnd('⚡ TOTAL PRINT TIME');
      return true;
    } catch (error) {
      console.error('Receipt print failed:', error);
      console.timeEnd('⚡ TOTAL PRINT TIME');
      throw error;
    }
  }

  async generateReceiptHTML(data, preGeneratedQR = null) {
    const {
      transactionId,
      cart = [],
      customerDetails = {},
      subtotal = 0,
      vat = 0,
      total = 0,
      discount = 0,
      paymentMethod = 'cash',
      applyVAT = false,
      timestamp = new Date(),
      cashier = 'Staff'
    } = data;

    // Extract first name from cashier
    const cashierFirstName = this.getDisplayName(cashier) || 'Staff';

    const dateStr = timestamp.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
    
    const timeStr = timestamp.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });

    const discountAmount = discount > 0 ? (subtotal + vat) * (discount / 100) : 0;
    const receiptId = `EP-${timestamp.getFullYear()}${String(timestamp.getMonth()+1).padStart(2,'0')}${String(timestamp.getDate()).padStart(2,'0')}-${transactionId?.slice(-6) || '000000'}`;
    
    let paymentText = 'Cash';
    if (paymentMethod) {
      switch(paymentMethod.toLowerCase()) {
        case 'cash': paymentText = 'Cash'; break;
        case 'card': paymentText = 'Card'; break;
        case 'bank_transfer': paymentText = 'Bank Transfer'; break;
        default: paymentText = paymentMethod.replace('_', ' ').toUpperCase();
      }
    }

    // Generate items rows
    const itemsRows = cart.map((item) => {
      const qty = item.cartQty || item.quantity || 1;
      const price = item.price || 0;
      const itemTotal = price * qty;
      
      return `
        <tr>
          <td style="padding: 4px 2px; font-size: 10px; font-weight: 700; border-bottom: 1px solid #ddd;">${qty}x</td>
          <td style="padding: 4px 4px; font-size: 10px; border-bottom: 1px solid #ddd;">
            <b>${item.name || 'Item'}</b><br>
            <span style="font-size: 9px; color: #666;">@ ₦${this.formatNumber(price)}</span>
          </td>
          <td align="right" style="padding: 4px 2px; font-size: 10px; font-weight: 700; border-bottom: 1px solid #ddd;">
            ₦${this.formatNumber(itemTotal)}
          </td>
        </tr>
      `;
    }).join('');

    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>Receipt - ${receiptId}</title>
    <style>
        /* MAC CHROME OPTIMIZED - MINIMAL CSS FOR FAST RENDERING */
        @page {
            size: 80mm auto;
            margin: 0;
        }
        
        @media print {
            body { margin: 0; padding: 2mm; }
        }
        
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: Helvetica, Arial, sans-serif;
            font-size: 11px;
            line-height: 1.3;
            width: 302px;
            margin: 0 auto;
            padding: 8px;
            background: #fff;
            color: #000;
        }
        
        table {
            width: 100%;
            border-collapse: collapse;
        }
        
        .logo-container {
            text-align: center;
            padding: 10px 0;
            border: 2px solid #000;
            margin-bottom: 8px;
        }
        
        .business-name {
            font-size: 18px;
            font-weight: bold;
            margin: 6px 0 2px 0;
        }
        
        .tagline {
            font-size: 10px;
            color: #333;
            margin-bottom: 6px;
        }
        
        .contact-info {
            font-size: 9px;
            color: #444;
            line-height: 1.4;
        }
        
        .divider {
            border-top: 2px solid #000;
            margin: 8px 0;
        }
        
        .info-box {
            background: #f5f5f5;
            border: 1px solid #000;
            padding: 8px;
            margin: 8px 0;
        }
        
        .info-box td {
            font-size: 10px;
            padding: 2px 0;
        }
        
        .label {
            font-weight: 700;
        }
        
        .items-table {
            margin: 8px 0;
        }
        
        .items-table th {
            background: #000;
            color: #fff;
            padding: 6px 4px;
            font-size: 10px;
            font-weight: 700;
            text-align: left;
        }
        
        .totals-box {
            background: #f5f5f5;
            border: 2px solid #000;
            padding: 10px;
            margin: 8px 0;
        }
        
        .totals-box td {
            padding: 3px 0;
            font-size: 11px;
        }
        
        .grand-total {
            border-top: 2px solid #000;
            padding-top: 6px !important;
            font-size: 14px !important;
            font-weight: 700;
        }
        
        .thank-you {
            background: #000;
            color: #fff;
            text-align: center;
            padding: 10px;
            margin: 8px 0;
        }
        
        .thank-you-text {
            font-size: 13px;
            font-weight: 700;
        }
        
        .thank-you-sub {
            font-size: 9px;
            margin-top: 3px;
        }
        
        .qr-container {
            text-align: center;
            padding: 10px;
            border: 2px dashed #000;
            margin: 8px 0;
        }
        
        .qr-title {
            font-size: 10px;
            font-weight: 600;
            margin-bottom: 4px;
        }
        
        .qr-container img {
            width: 100px;
            height: 100px;
            border: 2px solid #000;
        }
        
        .footer {
            text-align: center;
            border-top: 2px dashed #666;
            padding-top: 8px;
            margin-top: 8px;
            font-size: 9px;
            color: #666;
        }
        
        .powered-by {
            font-size: 10px;
            font-weight: 700;
            color: #000;
            margin-top: 4px;
        }
    </style>
</head>
<body>
    <!-- Logo & Header -->
    <div class="logo-container">
        ${this.logoSVG}
        <div class="business-name">${this.businessInfo.name}</div>
        <div class="tagline">${this.businessInfo.tagline}</div>
        <hr style="border: none; border-top: 1px solid #ccc; margin: 6px 20px;">
        <div class="contact-info">
            ${this.businessInfo.address}<br>
            ${this.businessInfo.city}<br>
            &#128222; ${this.businessInfo.phone}<br>
            &#127760; ${this.businessInfo.website}
        </div>
    </div>

    <div class="divider"></div>

    <!-- Transaction Info -->
    <div class="info-box">
        <table>
            <tr>
                <td class="label">Receipt:</td>
                <td align="right">${receiptId}</td>
            </tr>
            <tr>
                <td class="label">Date:</td>
                <td align="right">${dateStr}</td>
            </tr>
            <tr>
                <td class="label">Time:</td>
                <td align="right">${timeStr}</td>
            </tr>
            <tr>
                <td class="label">Cashier:</td>
                <td align="right">${cashierFirstName}</td>
            </tr>
            ${customerDetails?.name ? `
            <tr>
                <td class="label">Customer:</td>
                <td align="right">${customerDetails.name}</td>
            </tr>
            ` : ''}
        </table>
    </div>

    <!-- Items -->
    <div style="font-size: 11px; font-weight: 700; margin: 8px 0; text-transform: uppercase;">
        ITEMS PURCHASED
    </div>
    
    <table class="items-table">
        <thead>
            <tr>
                <th style="width: 15%;">QTY</th>
                <th style="width: 55%;">ITEM</th>
                <th style="width: 30%;" align="right">TOTAL</th>
            </tr>
        </thead>
        <tbody>
            ${itemsRows}
        </tbody>
    </table>

    <div class="divider"></div>

    <!-- Totals -->
    <div class="totals-box">
        <table>
            <tr>
                <td class="label">Subtotal:</td>
                <td align="right"><b>₦${this.formatNumber(subtotal)}</b></td>
            </tr>
            ${applyVAT && vat > 0 ? `
            <tr>
                <td class="label">VAT (7.5%):</td>
                <td align="right"><b>₦${this.formatNumber(vat)}</b></td>
            </tr>
            ` : ''}
            ${discount > 0 ? `
            <tr>
                <td class="label">Discount (${discount}%):</td>
                <td align="right"><b>-₦${this.formatNumber(discountAmount)}</b></td>
            </tr>
            ` : ''}
            <tr class="grand-total">
                <td class="label">TOTAL:</td>
                <td align="right"><b>₦${this.formatNumber(total)}</b></td>
            </tr>
        </table>
    </div>

    <!-- Payment -->
    <div style="background: #f9f9f9; border: 1px solid #ccc; padding: 8px; margin: 8px 0;">
        <table>
            <tr>
                <td class="label" style="font-size: 10px;">Payment Method:</td>
                <td align="right" style="font-size: 10px;"><b>${paymentText}</b></td>
            </tr>
        </table>
    </div>

    <!-- Thank You -->
    <div class="thank-you">
        <div class="thank-you-text">
            ${customerDetails?.name && customerDetails.name.trim() ? 
                `Thank you, ${customerDetails.name}!` : 
                `Thank you for visiting!`
            }
        </div>
        <div class="thank-you-sub">Your satisfaction is our priority</div>
    </div>

    <!-- QR Code -->
    ${preGeneratedQR ? `
    <div class="qr-container">
        <div class="qr-title" style="margin-bottom: 4px;">Visit us online for exclusive offers and services</div>
        <div style="font-size: 12px; font-weight: 700; margin-bottom: 6px;">ellasplace.net</div>
        <img src="${preGeneratedQR}" alt="QR Code" />
    </div>
    ` : ''}

    <!-- Footer -->
    <div class="footer">
        Visit us again soon!<br>
        Please keep this receipt for your records
        <div class="powered-by">
            Powered by Opsuite.io<br>
            &#128241; 09052463435
        </div>
    </div>
</body>
</html>
    `;
  }

  formatNumber(num) {
    return Math.round(num).toLocaleString('en-NG');
  }

  async printDirectly(htmlContent) {
    return new Promise((resolve, reject) => {
      try {
        // Create hidden iframe (but optimized for Mac Chrome)
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.top = '-9999px';
        iframe.style.left = '-9999px';
        iframe.style.width = '302px';
        iframe.style.height = '600px'; // Taller height helps Chrome
        
        document.body.appendChild(iframe);
        
        const doc = iframe.contentDocument || iframe.contentWindow.document;
        doc.open();
        doc.write(htmlContent);
        doc.close();
        
        // CRITICAL: Mac Chrome optimization
        // Wait for complete render before printing
        iframe.onload = () => {
          console.log('✅ Receipt HTML loaded');
          
          // STRATEGY 1: Let Chrome breathe (requestAnimationFrame)
          requestAnimationFrame(() => {
            // STRATEGY 2: Another frame for good measure
            requestAnimationFrame(() => {
              // STRATEGY 3: Small timeout to ensure render complete
              setTimeout(() => {
                console.log('✅ Opening print dialog...');
                
                try {
                  iframe.contentWindow.focus();
                  iframe.contentWindow.print();
                  
                  // Clean up after print
                  setTimeout(() => {
                    document.body.removeChild(iframe);
                    resolve();
                  }, 500);
                  
                } catch (printError) {
                  console.error('Print error:', printError);
                  document.body.removeChild(iframe);
                  reject(printError);
                }
              }, 200); // 200ms delay for Chrome to finish rendering
            });
          });
        };
        
        iframe.onerror = (error) => {
          console.error('iframe error:', error);
          document.body.removeChild(iframe);
          reject(error);
        };
        
      } catch (error) {
        console.error('printDirectly error:', error);
        reject(error);
      }
    });
  }
}

export default ReceiptPrinterBW;